import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { documentsApi } from "../../../modules/documents/api";
import { CreateUpdateDocumentNode } from "../../../modules/documents/types";
import { UUID } from "../../../typings/global";
import { EntityIdObject, EntityObject } from "../../types";

export const DOCUMENTS_QUERY_KEYS = {
  createDocumentNode: () => ["createDocumentNode"],
  uploadDocumentNodes: () => ["uploadDocumentNodes"],
  updateDocumentNode: () => ["updateDocumentNode"],
  deleteDocumentNode: () => ["deleteDocumentNode"],
  getDocumentNodeTree: () => ["getDocumentNodeTree"],
  downloadDocumentNode: () => ["downloadDocumentNode"],
};

export const useCreateDocumentNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_QUERY_KEYS.createDocumentNode(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateDocumentNode) => {
      const { data } = await documentsApi.createDocumentNode(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.getDocumentNodeTree() });
    },
  });
};

export const useUploadDocumentNodes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_QUERY_KEYS.uploadDocumentNodes(),
    mutationFn: async (payload: FormData) => {
      const { data } = await documentsApi.uploadDocumentNodes(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.getDocumentNodeTree() });
    },
  });
};

export const useUpdateDocumentNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_QUERY_KEYS.updateDocumentNode(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateDocumentNode>) => {
      const { data } = await documentsApi.updateDocumentNode(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.getDocumentNodeTree() });
    },
  });
};

export const useDeleteDocumentNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_QUERY_KEYS.deleteDocumentNode(),
    mutationFn: (id: UUID) => {
      return documentsApi.deleteDocumentNode({ id });
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.getDocumentNodeTree() });
    },
  });
};

export const useGetDocumentNodeTree = () => {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.getDocumentNodeTree(),
    queryFn: async () => {
      const { data } = await documentsApi.getDocumentNodeTree();

      return data;
    },
    staleTime: 0,
  });
};

export const useDownloadDocumentNode = () => {
  return useMutation({
    mutationKey: DOCUMENTS_QUERY_KEYS.downloadDocumentNode(),
    mutationFn: async (payload: EntityIdObject) => {
      const response = await documentsApi.downloadDocumentNode(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};
